<template>
  <!-- One risk scenario on an asset (US 9370): inherent risk folded away, an update of the residual risk,
       and every residual risk entry over time -->
  <V2SidePanel ref="panel"
               :kind="$t('risk_tab.kind')"
               :title="item && item.scenario ? item.scenario.name : ''"
               :intro="intro"
               :busy="saving"
               qid="risk-scenario-panel"
               @hidden="$emit('hidden')">
    <template v-if="item && rating">
      <div class="rk-panel__chips">
        <span class="rk-chip" :class="cia ? 'is-cia' : 'is-simple'">{{ $t('risk_tab.methods.' + item.method) }}</span>
        <span v-if="item.scenario && item.scenario.category" class="sp-chip">{{ item.scenario.category }}</span>
        <span class="sp-grow"></span>
        <div v-if="item.privacy_included" class="v2-seg">
          <span v-for="key in ['business', 'privacy']" :key="key" :class="{ on: type === key }" :qid="'risk-panel-type-' + key" @click="setType(key)">{{ $t('risk_tab.types.' + key) }}<i v-if="pending.includes(key)" class="rk-pending" :title="$t('risk_tab.unsaved')"></i></span>
        </div>
      </div>
      <b-alert v-if="error" show variant="danger">{{ error }}</b-alert>

      <!-- Inherent risk: set when the scenario was added, rarely changed afterwards -->
      <div class="rk-inh" qid="risk-inherent">
        <div class="rk-inh__top">
          <font-awesome-icon icon="lock"/>
          <div class="sp-grow">
            <b>{{ $t('risk_tab.inherent') }} · <RiskScore small :value="rating.inherent.score" :band="band(scale, rating.inherent.score)"></RiskScore>
              <span v-if="rating.inherent.score">{{ $t('risk_tab.bands.' + band(scale, rating.inherent.score)) }}</span></b>
            <small>{{ inherentText }}</small>
          </div>
          <button type="button" class="v2-link" qid="risk-inherent-toggle" @click="showInherent = !showInherent">{{ showInherent ? $t('risk_tab.hide') : $t('risk_tab.show') }}</button>
        </div>
        <div v-if="showInherent" class="rk-inh__body">
          <RiskRatingForm v-model="inherentForm" :scale="scale" :method="item.method" :disabled="!editingInherent" qid="risk-inherent-form"></RiskRatingForm>
          <div v-if="canEdit" class="rk-inh__actions">
            <template v-if="editingInherent">
              <b-button size="sm" variant="secondary" @click="cancelInherent">{{ $t('systems.create.cancel') }}</b-button>
              <b-button size="sm" variant="success" :disabled="saving" qid="risk-inherent-save" @click="saveInherent">{{ $t('system_hero.panel.save') }}</b-button>
            </template>
            <button v-else type="button" class="v2-link" qid="risk-inherent-change" @click="editingInherent = true">
              <font-awesome-icon icon="pen"/>{{ $t('risk_tab.change_inherent') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Update residual risk -->
      <div v-if="canEdit" class="rk-upd" qid="risk-update">
        <h5>
          <font-awesome-icon icon="sync-alt"/>{{ $t('risk_tab.update_title') }}
          <span class="sp-grow"></span>
          <small v-if="rating.residual">{{ $t('risk_tab.last', { score: rating.residual.score, date: formatDate(rating.residual.date) }) }}</small>
        </h5>
        <RiskRatingForm v-model="form" :scale="scale" :method="item.method" qid="risk-update-form"></RiskRatingForm>
        <div class="rk-upd__row">
          <span class="rk-upd__lab">{{ $t('risk_tab.why') }}</span>
          <b-form-textarea v-model="description" rows="2" max-rows="6" :placeholder="$t('risk_tab.why_placeholder')" qid="risk-update-why"></b-form-textarea>
        </div>
        <div class="rk-upd__row">
          <span class="rk-upd__lab">{{ $t('risk_tab.response') }}</span>
          <div class="sx-toggles">
            <button v-for="value in responses" :key="value" type="button" :class="{ on: response === value }" @click="response = response === value ? null : value">
              {{ $t('risk.risk_response.' + value.toLowerCase()) }}
            </button>
          </div>
        </div>
        <div v-if="newScore" class="rk-result" :class="'is-' + band(scale, newScore)" qid="risk-update-result">
          {{ $t('risk_tab.residual') }} <RiskScore small :value="newScore" :band="band(scale, newScore)"></RiskScore>
          <span v-if="rating.residual && newScore !== rating.residual.score" class="rk-delta" :class="newScore < rating.residual.score ? 'is-down' : 'is-up'">
            {{ newScore < rating.residual.score ? '▼' : '▲' }} {{ $t('risk_tab.from', { count: Math.abs(newScore - rating.residual.score), score: rating.residual.score }) }}
          </span>
          <span class="sp-grow"></span>
          <small v-if="rating.tolerance">{{ newScore > rating.tolerance ? $t('risk_tab.above_tolerance_n', { score: rating.tolerance }) : $t('risk_tab.within_tolerance_n', { score: rating.tolerance }) }}</small>
        </div>
      </div>

      <!-- Residual risk over time -->
      <section class="rk-log" qid="risk-log">
        <h5><font-awesome-icon icon="chart-line"/>{{ $t('risk_tab.log_title') }}<span class="sp-grow"></span><small>{{ $tc('risk_tab.updates', rating.history.length, { count: rating.history.length }) }}</small></h5>
        <RiskSparkline v-if="rating.history.length > 1" :points="rating.history.map(entry => entry.score)" :max="scale.max" :tolerance="rating.tolerance" :width="540" :height="64"></RiskSparkline>
        <div v-for="(entry, index) in log" :key="entry.id" class="rk-ev">
          <span class="rk-ev__dot" :class="'is-' + band(scale, entry.score)"></span>
          <div>
            <div class="rk-ev__w">
              {{ entry.score }} · {{ $t('risk_tab.bands.' + band(scale, entry.score)) }}
              <span v-if="log[index + 1]" class="rk-delta" :class="deltaClass(entry.score - log[index + 1].score)">{{ deltaText(entry.score - log[index + 1].score) }}</span>
            </div>
            <div class="rk-ev__m">
              <template v-if="entry.cia">{{ ciaText(entry.parts) }}</template>
              <template v-else>{{ $t('impact.' + levelKey(entry.impact)) }} × {{ $t('probability.' + levelKey(entry.probability)) }}</template>
              <template v-if="entry.risk_response"> · {{ $t('risk.risk_response.' + entry.risk_response.toLowerCase()) }}</template>
            </div>
            <div v-if="entry.description" class="rk-ev__m">{{ entry.description }}</div>
          </div>
          <div class="rk-ev__r">{{ formatDate(entry.date) }}<br>{{ entry.by }}</div>
        </div>
        <p v-if="!log.length" class="sp-sub">{{ $t('risk_tab.no_log') }}</p>
      </section>
    </template>

    <template #foot>
      <button v-if="canDelete && item" type="button" class="v2-link is-danger" qid="risk-remove" @click="remove">
        <font-awesome-icon icon="unlink"/>{{ $t('risk_tab.remove') }}
      </button>
      <span class="v2-panel__grow"></span>
      <b-button variant="secondary" @click="$refs.panel.hide()">{{ $t('system.close') }}</b-button>
      <b-button v-if="canEdit" variant="success" :disabled="saving || !pending.length" qid="risk-update-save" @click="saveResidual">
        <font-awesome-icon :icon="saving ? 'spinner' : 'check'" :class="{ 'fa-spin': saving }"/> {{ $t('risk_tab.save_update') }}
      </b-button>
    </template>
  </V2SidePanel>
</template>

<script>
  import moment from 'moment';
  import piincHttp from '@/util/configureAxios.js';
  import V2SidePanel from '@/components/V2SidePanel.vue';
  import RiskScore from '@/components/risk/RiskScore.vue';
  import RiskSparkline from '@/components/risk/RiskSparkline.vue';
  import RiskRatingForm from '@/components/risk/RiskRatingForm.vue';
  import {band, score, ciaScore, levelKey, CIA, METHOD_CIA, RESPONSES} from '@/components/risk/riskScale.js';

  const TYPE_VALUES = { business: 'business risk', privacy: 'privacy risk' };

  export default {
    name: 'RiskScenarioPanel',
    components: {
      V2SidePanel,
      RiskScore,
      RiskSparkline,
      RiskRatingForm
    },
    props: {
      asset: { type: String, required: true },
      object: { type: Object, required: true },
      scale: { type: Object, required: true },
      canEdit: { type: Boolean, default: false },
      canDelete: { type: Boolean, default: false }
    },
    data: function() {
      return {
        item: null,
        type: 'business',
        form: {},
        inherentForm: {},
        description: '',
        response: null,
        showInherent: false,
        editingInherent: false,
        drafts: {},
        saving: false,
        error: '',
        responses: RESPONSES
      }
    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
      cia() {
        return this.item.method === METHOD_CIA;
      },
      rating() {
        return this.item ? this.item.ratings[this.type] : null;
      },
      intro() {
        if (!this.item) {
          return '';
        }
        let parts = [this.object.name || this.object.title];
        if (this.rating && this.rating.tolerance) {
          parts.push(this.$t('risk_tab.tolerance', { score: this.rating.tolerance }));
        }
        return parts.join(' · ');
      },
      inherentText() {
        let inherent = this.rating.inherent;
        if (!inherent.score) {
          return this.$t('risk_tab.inherent_missing');
        }
        let text = this.cia ? this.ciaText(inherent.parts) : this.$t('impact.' + levelKey(inherent.impact)) + ' × ' + this.$t('probability.' + levelKey(inherent.probability));
        return text + (this.item.created_at ? ' · ' + this.$t('risk_tab.set_on', { date: this.formatDate(this.item.created_at) }) : '');
      },
      newScore() {
        return this.cia ? ciaScore(this.scale, this.form) : score(this.scale, this.form.impact, this.form.probability);
      },
      // Types with an unsaved update: the open one as it is now, the other from its draft
      pending() {
        if (!this.item) {
          return [];
        }
        let current = { form: this.form, description: this.description, response: this.response };
        return ['business', 'privacy'].filter(type => this.changed(type, type === this.type ? current : this.drafts[type]));
      },
      // Newest first
      log() {
        return this.rating.history.slice().reverse();
      }
    },
    methods: {
      band: band,
      levelKey: levelKey,
      show(item, type) {
        this.item = item;
        this.type = item.ratings[type] ? type : 'business';
        this.showInherent = false;
        this.drafts = {};
        this.reset();
        this.$refs.panel.show();
      },
      // New data for the open scenario after a save
      refresh(item) {
        if (item) {
          this.item = item;
          this.drafts = {};
          this.reset();
        }
      },
      // Business and privacy each keep their own unsaved update, so switching between them loses nothing
      setType(type) {
        this.keepDraft();
        this.type = type;
        let draft = this.drafts[type];
        if (draft) {
          this.form = draft.form;
          this.description = draft.description;
          this.response = draft.response;
          this.inherentForm = this.inherentToForm();
          this.editingInherent = false;
        } else {
          this.reset();
        }
      },
      keepDraft() {
        this.$set(this.drafts, this.type, { form: this.form, description: this.description, response: this.response });
      },
      // A draft counts when it scores and differs from the latest entry, or says why or how
      changed(type, draft) {
        let rating = this.item.ratings[type];
        if (!rating || !draft) {
          return false;
        }
        let value = this.cia ? ciaScore(this.scale, draft.form) : score(this.scale, draft.form.impact, draft.form.probability);
        if (!value) {
          return false;
        }
        let latest = rating.residual;
        let original = JSON.stringify(this.toForm(latest));
        return JSON.stringify(draft.form) !== original || !!draft.description || (draft.response || null) !== ((latest && latest.risk_response) || null);
      },
      // The update starts from the latest residual risk; the inherent form from the inherent risk
      reset() {
        let latest = this.rating.residual;
        this.form = this.toForm(latest);
        this.inherentForm = this.inherentToForm();
        this.description = '';
        this.response = latest ? latest.risk_response : null;
        this.editingInherent = false;
        this.error = '';
      },
      payload(type, draft) {
        let data = { type: TYPE_VALUES[type], description: draft.description || null, risk_response: draft.response || null };
        if (this.cia) {
          CIA.forEach(key => {
            data[key + '_impact'] = draft.form[key].impact || null;
            data[key + '_probability'] = draft.form[key].probability || null;
          });
        } else {
          data.impact = draft.form.impact;
          data.probability = draft.form.probability;
        }
        return data;
      },
      toForm(entry) {
        if (!entry) {
          return this.cia ? { confidentiality: {}, integrity: {}, availability: {} } : { impact: null, probability: null };
        }
        if (this.cia) {
          let form = {};
          CIA.forEach(key => { form[key] = Object.assign({}, (entry.cia && entry.cia[key]) || {}); });
          return form;
        }
        return { impact: entry.impact, probability: entry.probability };
      },
      inherentToForm() {
        let inherent = this.rating.inherent;
        if (this.cia) {
          let form = {};
          CIA.forEach(key => {
            let pair = (inherent.cia && inherent.cia[key]) || {};
            form[key] = { impact: pair.impact || null, probability: pair.probability || null };
          });
          return form;
        }
        return { impact: this.rating.inherent.impact, probability: this.rating.inherent.probability };
      },
      ciaText(parts) {
        if (!parts) {
          return '';
        }
        return CIA.map(key => this.$t('systems_overview.criticality.' + key).slice(0, 1) + ' ' + (parts[key] || '–')).join(' · ');
      },
      deltaClass(change) {
        return change < 0 ? 'is-down' : (change > 0 ? 'is-up' : 'is-flat');
      },
      deltaText(change) {
        if (!change) {
          return this.$t('risk_tab.no_change');
        }
        return (change < 0 ? '▼ ' : '▲ ') + Math.abs(change);
      },
      formatDate(value) {
        return value ? moment(value).format('D MMM YYYY') : '';
      },
      path() {
        return this.asset + '/' + this.object.slug + '/risk-scenarios/' + this.item.link_id;
      },
      cancelInherent() {
        this.inherentForm = this.inherentToForm();
        this.editingInherent = false;
      },
      // Changing the inherent risk goes through the scenario link, which logs the change
      saveInherent() {
        let self = this;
        let prefix = this.type + '_';
        let data = {};
        if (this.cia) {
          CIA.forEach(key => {
            data[prefix + key + '_risk_impact'] = this.inherentForm[key].impact || '';
            data[prefix + key + '_risk_probability'] = this.inherentForm[key].probability || '';
          });
        } else {
          data[prefix + 'inherent_risk_impact'] = this.inherentForm.impact || '';
          data[prefix + 'inherent_risk_probability'] = this.inherentForm.probability || '';
        }
        this.saving = true;
        piincHttp.put(this.path(), data).then(function() {
          self.saving = false;
          self.editingInherent = false;
          self.$toastr('success', self.$t('activities.risk_scenario.update.success'));
          self.$emit('changed');
        }, function(error) {
          self.saving = false;
          self.error = (error.data && error.data.message) || self.$t('system_hero.panel.error');
        });
      },
      // Saves the business and the privacy update when both have one, one after the other
      saveResidual() {
        let self = this;
        this.keepDraft();
        let types = this.pending;
        this.saving = true;
        this.error = '';
        types.reduce((chain, type) => chain.then(() => piincHttp.post('risk/' + this.asset + '/' + this.object.id + '/scenarios/' + this.item.link_id + '/residual',
          this.payload(type, this.drafts[type]))), Promise.resolve()).then(function() {
          self.saving = false;
          self.$toastr('success', self.$t('risk_tab.saved'));
          self.$emit('changed');
        }, function(error) {
          self.saving = false;
          self.error = (error && error.data && error.data.message) || self.$t('system_hero.panel.error');
        });
      },
      remove() {
        let self = this;
        this.$bvModal.msgBoxConfirm(this.$t('risk_tab.remove_text', { scenario: this.item.scenario ? this.item.scenario.name : '', name: this.object.name || this.object.title }), {
          title: this.$t('risk_tab.remove_title'),
          okTitle: this.$t('risk_tab.remove'),
          okVariant: 'danger',
          cancelTitle: this.$t('systems.create.cancel'),
          centered: true
        }).then(function(ok) {
          if (!ok) {
            return;
          }
          piincHttp.delete(self.path()).then(function() {
            self.$toastr('success', self.$t('risk_tab.removed'));
            self.$refs.panel.hide();
            self.$emit('changed');
          }, function() {});
        });
      }
    }
  }
</script>
